import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LifeBuoy, Mail, Info, ExternalLink } from "lucide-react";
import PartnerMediaManager from "./PartnerMediaManager";

// ---------- Reusable page wrapper ----------
const Page = ({ title, subtitle, right, children, testid }) => (
  <div className="max-w-6xl space-y-6" data-testid={testid}>
    <div className="flex items-baseline justify-between flex-wrap gap-3">
      <div>
        <h1 className="text-2xl font-bold">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

// ---------- Students ----------
export function PartnerStudentsPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/api/partner/students").then(({ data }) => setRows(data)); }, []);
  return (
    <Page title="Students" subtitle="Everyone enrolled in your courses." testid="partner-students-page">
      {rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500" data-testid="partner-students-empty">
          No students yet. Publish a course and share the link to start enrolling learners.
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0"><table className="w-full text-sm" data-testid="partner-students-table">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Active</th><th className="p-3">Completed</th></tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="p-3 font-semibold">{r.full_name}</td>
                <td className="p-3 text-gray-600">{r.email}</td>
                <td className="p-3">{r.active_count}</td>
                <td className="p-3">{r.completed_count}</td>
              </tr>
            ))}
          </tbody>
        </table></CardContent></Card>
      )}
    </Page>
  );
}

// ---------- Registrations ----------
export function PartnerRegistrationsPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/api/partner/registrations").then(({ data }) => setRows(data)); }, []);
  return (
    <Page title="Registrations" subtitle="Recent course enrollments." testid="partner-registrations-page">
      {rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500" data-testid="partner-registrations-empty">No registrations yet.</CardContent></Card>
      ) : (
        <Card><CardContent className="p-0"><table className="w-full text-sm" data-testid="partner-registrations-table">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
            <tr><th className="p-3">Student</th><th className="p-3">Course</th><th className="p-3">Date</th><th className="p-3">Progress</th></tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="p-3">
                  <div className="font-semibold">{r.user_name}</div>
                  <div className="text-xs text-gray-500">{r.user_email}</div>
                </td>
                <td className="p-3">{r.course_title}</td>
                <td className="p-3 text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-3">
                  {r.completed ? <Badge className="bg-emerald-100 text-emerald-800">Completed</Badge>
                              : <span className="text-xs">{r.progress_pct || 0}%</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table></CardContent></Card>
      )}
    </Page>
  );
}

// ---------- Assessments ----------
export function PartnerAssessmentsPage() {
  const [courses, setCourses] = useState([]);
  useEffect(() => { api.get("/api/partner/courses").then(({ data }) => setCourses(data)); }, []);
  const withAssess = courses.filter((c) => c.final_test || (c.modules || []).some((m) => m.quiz));
  return (
    <Page title="Assessments" subtitle="Quizzes and final tests across your courses." testid="partner-assessments-page">
      {withAssess.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500" data-testid="partner-assessments-empty">
          None of your courses have quizzes or a final test yet. Open a course from "My Courses" and add a `quiz` block to any module.
        </CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {withAssess.map((c) => {
            const qz = (c.modules || []).filter((m) => m.quiz).length;
            return (
              <Card key={c.id}>
                <CardHeader><CardTitle className="text-base">{c.title}</CardTitle></CardHeader>
                <CardContent className="text-sm space-y-1">
                  <div>{qz} module quiz{qz === 1 ? "" : "es"}</div>
                  <div>Final test: {c.final_test ? "Yes" : "No"}</div>
                  <Link to={`/partner/courses/${c.id}/edit`} className="text-xs text-[#DC2626] hover:underline">
                    Edit course →
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </Page>
  );
}

// ---------- Certificates ----------
export function PartnerCertificatesPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/api/partner/certificates").then(({ data }) => setRows(data)); }, []);
  return (
    <Page title="Certificates" subtitle="Every dual-brand certificate you've issued." testid="partner-certificates-page">
      {rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500" data-testid="partner-certificates-empty">
          No certificates issued yet — they're generated automatically when a student passes your final test.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => (
            <Card key={c.id}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{c.student_name} — {c.course_title}</div>
                  <div className="text-xs text-gray-500">{c.cert_number} · Issued {new Date(c.issued_at).toLocaleDateString()}</div>
                </div>
                <Badge className="bg-emerald-100 text-emerald-800">Score: {c.final_score}%</Badge>
                <a href={`/verify/${c.id}`} target="_blank" rel="noreferrer"
                  className="text-xs text-[#DC2626] hover:underline">Verify</a>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}

// ---------- Trainers (placeholder for Phase 2 partner_staff) ----------
export function PartnerTrainersPage() {
  return (
    <Page title="Trainers" subtitle="Invite team members to help you build and teach." testid="partner-trainers-page">
      <Card className="border-dashed">
        <CardContent className="p-8 text-center">
          <Info className="w-8 h-8 text-[#F59E0B] mx-auto mb-3" />
          <div className="font-semibold mb-1">Multi-seat trainers is coming in Phase 2</div>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Right now, one login runs your whole partner account. Multi-seat invites for
            trainers, admins, and reviewers will land in the next release. Need it sooner?
            Email <a className="text-[#DC2626] underline" href="mailto:teachmeonlineca@gmail.com">teachmeonlineca@gmail.com</a>.
          </p>
        </CardContent>
      </Card>
    </Page>
  );
}

// ---------- Media ----------
export function PartnerMediaPage() {
  return (
    <Page title="Media library" subtitle="All uploaded images, PDFs, and videos across your courses." testid="partner-media-page">
      <PartnerMediaManager />
    </Page>
  );
}

// ---------- Accreditations ----------
export function PartnerAccreditationsPage() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/api/partner-onboarding/accreditations").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  return (
    <Page title="Accreditations"
      subtitle="Manage the credentials that back your training."
      right={<Link to="/onboarding/partner"><Button variant="outline" size="sm" data-testid="acc-manage-btn">Add / manage →</Button></Link>}
      testid="partner-accreditations-page">
      {rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500" data-testid="partner-accreditations-empty">
          No accreditations yet. Use the onboarding wizard to add them.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <Card key={a.id}><CardContent className="p-4 flex items-center gap-3">
              <div className="flex-1">
                <div className="font-semibold text-sm">{a.accrediting_organization}</div>
                <div className="text-xs text-gray-500">
                  {a.accreditation_name || "—"}
                  {a.accreditation_number && ` · #${a.accreditation_number}`}
                </div>
              </div>
              <Badge className={a.verified ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                {a.verified ? "Verified" : "Pending verification"}
              </Badge>
            </CardContent></Card>
          ))}
        </div>
      )}
    </Page>
  );
}

// ---------- Documents ----------
export function PartnerDocumentsPage() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/api/partner-onboarding/state").then(({ data }) => setRows(data.documents || []));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/api/partner-onboarding/documents/${id}`);
    load();
  };
  return (
    <Page title="Documents"
      subtitle="Registration, licence, insurance & other supporting docs."
      right={<Link to="/onboarding/partner"><Button variant="outline" size="sm" data-testid="doc-upload-more">Upload more →</Button></Link>}
      testid="partner-documents-page">
      {rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500" data-testid="partner-documents-empty">
          No documents uploaded yet.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {rows.map((d) => (
            <Card key={d.id}><CardContent className="p-4 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{d.label || d.filename}</div>
                <div className="text-xs text-gray-500">{d.kind?.replace("_", " ")} · {d.size_mb} MB</div>
              </div>
              <button onClick={() => del(d.id)} className="text-xs text-red-600 hover:underline">Delete</button>
            </CardContent></Card>
          ))}
        </div>
      )}
    </Page>
  );
}

// ---------- Sales ----------
export function PartnerSalesPage() {
  const [data, setData] = useState({ total_usd: 0, transactions: [] });
  useEffect(() => { api.get("/api/partner/sales").then(({ data }) => setData(data)); }, []);
  return (
    <Page title="Sales" subtitle="Every course transaction tagged with your partner ID." testid="partner-sales-page">
      <Card>
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-gray-500">Total revenue (paid)</div>
          <div className="text-3xl font-bold text-[#064E3B] mt-1" data-testid="partner-sales-total">
            ${data.total_usd.toFixed(2)} USD
          </div>
          <div className="text-xs text-gray-500 mt-1">Manual revenue-share; automated payouts on the Phase 2 roadmap.</div>
        </CardContent>
      </Card>
      {data.transactions.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500" data-testid="partner-sales-empty">
          No transactions yet.
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0"><table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
            <tr><th className="p-3">Date</th><th className="p-3">Customer</th><th className="p-3">Amount</th><th className="p-3">Status</th></tr>
          </thead>
          <tbody className="divide-y">
            {data.transactions.map((t) => (
              <tr key={t.id}>
                <td className="p-3 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="p-3">{t.user_email || "—"}</td>
                <td className="p-3 font-semibold">${t.amount} {t.currency.toUpperCase()}</td>
                <td className="p-3"><Badge variant="secondary" className="text-xs">{t.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table></CardContent></Card>
      )}
    </Page>
  );
}

// ---------- Profile ----------
export function PartnerProfilePage() {
  const { partner, refreshPartner } = useOutletContext();
  const [form, setForm] = useState({
    organization_name: partner.organization_name || "",
    tagline: partner.tagline || "",
    about: partner.about || "",
    website: partner.website || "",
    contact_phone: partner.contact_phone || "",
    logo_url: partner.logo_url || "",
    cover_image_url: partner.cover_image_url || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/api/partner/profile", form);
      toast.success("Profile updated");
      await refreshPartner();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Page title="Profile" subtitle="Edit what students see on your storefront." testid="partner-profile-page">
      <Card><CardContent className="p-6 space-y-3">
        <div><Label className="text-xs">Organization name</Label><Input value={form.organization_name} onChange={set("organization_name")} className="mt-1" data-testid="profile-name-input" /></div>
        <div><Label className="text-xs">Tagline</Label><Input value={form.tagline} onChange={set("tagline")} className="mt-1" data-testid="profile-tagline-input" /></div>
        <div><Label className="text-xs">About</Label><Textarea rows={4} value={form.about} onChange={set("about")} className="mt-1" data-testid="profile-about-input" /></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Website</Label><Input value={form.website} onChange={set("website")} className="mt-1" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={form.contact_phone} onChange={set("contact_phone")} className="mt-1" /></div>
          <div><Label className="text-xs">Logo URL</Label><Input value={form.logo_url} onChange={set("logo_url")} className="mt-1" /></div>
          <div><Label className="text-xs">Cover image URL</Label><Input value={form.cover_image_url} onChange={set("cover_image_url")} className="mt-1" /></div>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy} data-testid="profile-save-btn" className="bg-[#DC2626] hover:bg-[#B91C1C]">
            {busy ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </CardContent></Card>
    </Page>
  );
}

// ---------- Support ----------
export function PartnerSupportPage() {
  return (
    <Page title="Support" subtitle="We're here to help." testid="partner-support-page">
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <LifeBuoy className="w-6 h-6 text-[#DC2626] mt-1" />
            <div>
              <div className="font-bold">Read the Partner Guide</div>
              <div className="text-sm text-gray-600 mb-1">6-page PDF covering the entire flow — apply, build a course, publish, dual certificates, compliance.</div>
              <a href="/teacher4me-partner-guide.pdf" target="_blank" rel="noreferrer"
                data-testid="support-download-guide"
                className="text-sm font-semibold text-[#DC2626] hover:underline inline-flex items-center gap-1">
                Download PDF <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
          <hr />
          <div className="flex items-start gap-3">
            <Mail className="w-6 h-6 text-[#DC2626] mt-1" />
            <div>
              <div className="font-bold">Email us</div>
              <div className="text-sm text-gray-600 mb-1">Response within 2 business days.</div>
              <a href="mailto:teachmeonlineca@gmail.com" data-testid="support-email-link"
                className="text-sm font-semibold text-[#DC2626] hover:underline">
                teachmeonlineca@gmail.com
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}
